/**
 * CandidatePortfolio — recruiter-curated portfolio of candidate links and
 * file attachments with per-item recruiter comments. Mounted on Overview.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Plus, Trash2, Upload, ExternalLink, FileText, Pencil, Save, X, Link as LinkIcon } from "lucide-react";

function shortDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; }
}

function fmtSize(b) {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 102.4) / 10} KB`;
    return `${Math.round(b / 104857.6) / 10} MB`;
}

export default function CandidatePortfolio({ candidateId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLink, setShowLink] = useState(false);
    const [link, setLink] = useState({ url: "", title: "", description: "" });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/candidates/${candidateId}/portfolio`);
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [candidateId]);

    const addLink = async () => {
        if (!link.url.trim()) return;
        try {
            await api.post(`/candidates/${candidateId}/portfolio/link`, link);
            setLink({ url: "", title: "", description: "" });
            setShowLink(false);
            load();
            toast.success("Link added");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 20 * 1024 * 1024) { toast.error("File exceeds 20 MB"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("title", f.name);
            await api.post(`/candidates/${candidateId}/portfolio/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            load();
            toast.success("File uploaded");
        } catch (err) { toast.error(formatApiError(err)); }
        finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <section className="border border-border bg-white" data-testid="portfolio">
            <header className="px-3 sm:px-4 py-2.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-ikb" />
                    <h3 className="font-display font-bold text-sm sm:text-base">Portfolio</h3>
                    <span className="text-[10px] font-mono text-muted-foreground">· {items.length}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setShowLink((v) => !v)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-secondary" data-testid="portfolio-add-link-btn">
                        <LinkIcon className="w-3 h-3" /> Add link
                    </button>
                    <label className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-secondary cursor-pointer" data-testid="portfolio-upload-btn">
                        <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload file"}
                        <input ref={fileRef} type="file" className="hidden" onChange={upload} disabled={uploading} />
                    </label>
                </div>
            </header>
            {showLink && (
                <div className="p-3 sm:p-4 border-b border-border bg-secondary/40 space-y-2" data-testid="portfolio-link-form">
                    <input type="url" value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://…" className="w-full h-9 px-2 border border-border bg-white text-sm rounded-none outline-none focus:border-ikb" data-testid="portfolio-link-url" />
                    <input type="text" value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} placeholder="Title (optional)" className="w-full h-9 px-2 border border-border bg-white text-sm rounded-none outline-none focus:border-ikb" data-testid="portfolio-link-title" />
                    <textarea value={link.description} onChange={(e) => setLink({ ...link, description: e.target.value })} placeholder="Notes about this link (optional)" rows={2} className="w-full px-2 py-1.5 border border-border bg-white text-sm rounded-none outline-none focus:border-ikb" data-testid="portfolio-link-desc" />
                    <div className="flex items-center gap-2">
                        <button onClick={addLink} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1.5 inline-flex items-center gap-1" data-testid="portfolio-link-save"><Save className="w-3 h-3" /> Add</button>
                        <button onClick={() => { setShowLink(false); setLink({ url: "", title: "", description: "" }); }} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                    </div>
                </div>
            )}
            {loading ? (
                <div className="p-6 text-center text-xs font-mono text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <div className="p-6 text-center text-xs font-mono text-muted-foreground">No portfolio items yet. Add links or upload files.</div>
            ) : (
                <ul className="divide-y divide-border" data-testid="portfolio-list">
                    {items.map((it) => <PortfolioRow key={it.id} item={it} candidateId={candidateId} onChanged={load} />)}
                </ul>
            )}
        </section>
    );
}

function PortfolioRow({ item, candidateId, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [comment, setComment] = useState(item.recruiter_comment || "");

    const remove = async () => {
        if (!window.confirm("Remove this item?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/portfolio/${item.id}`);
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async () => {
        try {
            await api.patch(`/candidates/${candidateId}/portfolio/${item.id}`, { recruiter_comment: comment });
            setEditing(false);
            onChanged();
            toast.success("Comment saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const isFile = item.kind === "file";
    const fileHref = isFile ? (item.file_url?.startsWith("http") ? item.file_url : `${API}${item.file_url}`) : item.url;

    return (
        <li className="p-3 sm:p-4 space-y-2" data-testid={`portfolio-row-${item.id}`}>
            <div className="flex items-start gap-2 flex-wrap">
                {isFile ? <FileText className="w-4 h-4 text-foreground/70 mt-1 shrink-0" /> : <LinkIcon className="w-4 h-4 text-foreground/70 mt-1 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <a href={fileHref} target="_blank" rel="noreferrer" className="text-sm font-medium text-ikb hover:underline inline-flex items-center gap-1 break-all" data-testid={`portfolio-link-${item.id}`}>
                        {item.title || item.filename || item.url} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    {item.description && <div className="text-[12px] text-foreground/80 mt-0.5">{item.description}</div>}
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                        {isFile ? `${item.content_type || "file"} · ${fmtSize(item.size)} · ` : ""}added by {item.added_by_name || "—"} · {shortDate(item.added_at)}
                    </div>
                </div>
                <button onClick={remove} className="p-1.5 border border-border text-rose-700 hover:bg-rose-50 shrink-0" title="Remove" data-testid={`portfolio-delete-${item.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
            {editing ? (
                <div className="border border-ikb/40 bg-blue-50/30 p-2 space-y-1.5">
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Recruiter comment…" autoFocus className="w-full text-[12px] border border-border bg-white p-1.5 rounded-none outline-none focus:border-ikb" data-testid={`portfolio-comment-textarea-${item.id}`} />
                    <div className="flex items-center gap-1.5">
                        <button onClick={save} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1 inline-flex items-center gap-1" data-testid={`portfolio-comment-save-${item.id}`}><Save className="w-3 h-3" /> Save</button>
                        <button onClick={() => { setEditing(false); setComment(item.recruiter_comment || ""); }} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                    </div>
                </div>
            ) : item.recruiter_comment ? (
                <div className="text-[12px] border-l-2 border-foreground/30 pl-2 py-1 italic" data-testid={`portfolio-comment-${item.id}`}>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-1">Recruiter comment:</span>{item.recruiter_comment}
                    <button onClick={() => setEditing(true)} className="ml-2 text-ikb hover:underline inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest" data-testid={`portfolio-comment-edit-${item.id}`}><Pencil className="w-2.5 h-2.5" /> Edit</button>
                </div>
            ) : (
                <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-widest text-ikb/70 hover:text-ikb hover:underline inline-flex items-center gap-1" data-testid={`portfolio-comment-add-${item.id}`}>
                    <Pencil className="w-2.5 h-2.5" /> Add recruiter comment
                </button>
            )}
        </li>
    );
}
